import { twMerge } from 'tailwind-merge';

import {
  Icon,
  MenuButton,
  MenuItem,
  RyeconCameraLens,
  Sizes,
} from '@dbt-labs/sourdough';

import { Lens } from '../dag/InternalDagInterfaces';
import { useDagContext } from '../dbt-dag';
import { Subcomponent } from '../utils/subcomponent';
import { LensLegends } from './LensLegend';

export type LensSwitcherProps = {
  className?: string | null;
  lenses: Lens[];
  onChange?: (lens: Lens | null) => void;
  onInteract?: () => void;
};

export const LensSwitcher: Subcomponent<LensSwitcherProps> = ({
  className,
  lenses,
  onChange,
  onInteract,
}) => {
  const { lens, setLens } = useDagContext();
  const menuItems: MenuItem[] = [
    {
      label: LensLegends.default.title,
      description: LensLegends.default.description,
      ryecon: LensLegends.default.titleIcon,
      value: 'default',
      isDisabled: lens == null,
    },
    ...(lenses ?? [])?.map((l) => ({
      label: LensLegends[l].title,
      description: LensLegends[l].description,
      ryecon: LensLegends[l].titleIcon,
      value: l,
      isDisabled: l === lens || !LensLegends[l].isSelectable,
    })),
  ];

  return (
    <div className={twMerge('pointer-events-auto flex whitespace-nowrap', className)}>
      <span className="flex select-none items-center rounded-l bg-bgBlack p-2 text-center text-fgWhite">
        <Icon ryecon={RyeconCameraLens} size="sm" className="mr-1" />
        <span className="font-label-lg leading-none">Lenses</span>
      </span>
      <span className="relative whitespace-nowrap" onClick={() => onInteract?.()}>
        <MenuButton
          testId="lens-switcher"
          size={Sizes.sm}
          className="h-8 rounded-l-none rounded-r"
          ryecon={LensLegends[lens || 'default'].titleIcon}
          text={LensLegends[lens || 'default'].title}
          items={menuItems}
          menuAlignment="left"
          menuPlacement="top"
          onClick={(value: Lens | 'default') => {
            const val = value === 'default' ? null : value;
            setLens(val);
            if (onChange && lens !== val) {
              onChange(val);
            }
          }}
        />
      </span>
    </div>
  );
};

LensSwitcher.displayName = 'LensSwitcher';
